import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import AdminDialog from "../../components/admin/AdminDialog";
import { Plus, Trash2, Pencil } from "lucide-react";

const blank = {
  code: "", description: "", discount_type: "percent", value: 10,
  max_discount: null, min_order: 0, usage_limit: null, per_user_limit: 1,
  start_at: "", end_at: "", is_active: true,
};

export default function SellerCoupons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/seller/coupons");
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(blank); setEditingId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...blank, ...c, start_at: c.start_at || "", end_at: c.end_at || "" }); setEditingId(c.id); setOpen(true); };

  const save = async () => {
    if (!/^[A-Za-z0-9_-]{3,32}$/.test(form.code)) return toast.error("Code must be 3-32 chars (A-Z, 0-9, _, -)");
    if (form.discount_type === "percent" && form.value > 100) return toast.error("Percent cannot exceed 100");
    const payload = { ...form, code: form.code.toUpperCase(), value: Number(form.value) };
    ["max_discount", "min_order", "usage_limit", "per_user_limit"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null) delete payload[k];
      else payload[k] = Number(payload[k]);
    });
    if (!payload.start_at) delete payload.start_at;
    if (!payload.end_at) delete payload.end_at;
    try {
      if (editingId) await api.put(`/seller/coupons/${editingId}`, payload);
      else await api.post("/seller/coupons", payload);
      toast.success(editingId ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await api.delete(`/seller/coupons/${id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="seller-coupons-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Marketing</div>
          <h1 className="font-display text-4xl">My Coupons</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Coupons that apply only to your store.</p>
        </div>
        <button className="kb-btn-primary" onClick={openNew} data-testid="seller-coupon-add">
          <Plus size={16} className="inline mr-1" /> New Coupon
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="coupons-loading">Loading coupons…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-700" data-testid="coupons-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]">No coupons yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
              <tr><th className="p-3">Code</th><th>Type</th><th>Value</th><th>Used</th><th>Min Order</th><th>Active</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`coupon-row-${r.code}`}>
                  <td className="p-3 font-mono">{r.code}</td>
                  <td className="p-3">{r.discount_type}</td>
                  <td className="p-3">{r.discount_type === "percent" ? `${r.value}%` : `₹${r.value}`}</td>
                  <td className="p-3">{r.used_count || 0}{r.usage_limit ? `/${r.usage_limit}` : ""}</td>
                  <td className="p-3">{r.min_order ? `₹${r.min_order}` : "—"}</td>
                  <td className="p-3 text-xs uppercase">{r.is_active ? "yes" : "no"}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(r)} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`coupon-edit-${r.code}`}><Pencil size={12} /></button>
                      <button onClick={() => remove(r.id)} className="text-red-700 p-1" data-testid={`coupon-del-${r.code}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Coupon"
        subtitle={editingId ? "Edit" : "New"}
        maxWidth="max-w-2xl"
        testid="seller-coupon-dialog"
        footer={
          <>
            <button className="kb-btn-outline" onClick={() => setOpen(false)}>Cancel</button>
            <button className="kb-btn-primary" onClick={save} data-testid="seller-coupon-save">{editingId ? "Update" : "Create"}</button>
          </>
        }
      >
        <div className="grid md:grid-cols-3 gap-3">
          <input className="kb-input" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code" />
          <select className="kb-input" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
            <option value="percent">Percent %</option>
            <option value="flat">Flat ₹</option>
          </select>
          <input type="number" className="kb-input" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          <input type="number" className="kb-input" placeholder="Min Order (₹)" value={form.min_order ?? ""} onChange={(e) => setForm({ ...form, min_order: e.target.value })} />
          <input type="number" className="kb-input" placeholder="Max Discount (₹)" value={form.max_discount ?? ""} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} />
          <input type="number" className="kb-input" placeholder="Usage Limit" value={form.usage_limit ?? ""} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} />
          <input type="date" className="kb-input" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
          <input type="date" className="kb-input" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} />
          <label className="flex items-center gap-2 text-sm pt-2">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
          </label>
        </div>
        <input className="kb-input" placeholder="Description (optional)" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </AdminDialog>
    </div>
  );
}
